import { MatrixEvent, MatrixEventEvent, Room, RoomEvent } from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useRef } from 'react';
import { Trans } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ReactComponent as At } from 'assets/icons/At.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { getFormattedGuestDisplayName } from 'modules/MeetingChat/Messages/Message/utils';
import { getAllMessengerRooms, isMessengerRoom } from 'modules/Messenger/components/ConversationsList/utils';
import { getUnreadMentionEventsForUser } from 'modules/Messenger/utils/roomHasUnreadMentionForUser';
import useNotifications from 'modules/Notifications/NotificationsProvider/useNotifications';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { switchWorkspaceForMessengerRoom } from 'utils/roomHierarchyUtils';

const useMentionNotifications = (): void => {
  const { matrixClient, hasReachedSyncReadyState } = useMatrixContext();
  const { addNotification, removeNotification, removeNotificationsByPrefix } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const { setWorkspaceId, workspaceId, workspacesState } = useWorkspacesContext();

  const workspaceContextRef = useRef({ workspaceId, workspaces: workspacesState.workspaces });
  workspaceContextRef.current = { workspaceId, workspaces: workspacesState.workspaces };

  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const tryAddMentionNotification = useCallback(
    (event: MatrixEvent, room: Room) => {
      const myUserId = matrixClient.getUserId();
      const senderId = event.getSender();
      if (!myUserId || !senderId || senderId === myUserId) return;
      if (!isMessengerRoom(room)) return;
      if (pathnameRef.current.startsWith(`/messenger/${room.roomId}`)) return;

      const eventId = event.getId();
      if (!eventId) return;

      const isUnreadMention = getUnreadMentionEventsForUser(room, matrixClient).some((e) => e.getId() === eventId);
      if (!isUnreadMention) return;

      const notificationId = `messenger-mention-${room.roomId}-${eventId}`;
      const senderName = getFormattedGuestDisplayName(room.getMember(senderId)?.rawDisplayName);
      const roomName = room.name;

      const title = (
        <Trans
          i18nKey="messenger.mentioned_you"
          components={{ bold: <span className="font-medium" /> }}
          values={{ name: senderName, room: roomName }}
        />
      );

      addNotification({
        id: notificationId,
        icon: At,
        variant: 'alert',
        title,
        content: null,
        isRead: false,
        timestamp: event.getTs() ?? Date.now(),
        onClick: () => {
          removeNotification(notificationId);
          navigate(`/messenger/${room.roomId}`, { state: { scrollToEventId: eventId } });
          const { workspaceId: currentWorkspaceId, workspaces } = workspaceContextRef.current;
          switchWorkspaceForMessengerRoom(matrixClient, room.roomId, workspaces, currentWorkspaceId, setWorkspaceId);
        },
      });
    },
    [addNotification, matrixClient, navigate, removeNotification, setWorkspaceId]
  );

  const hydrateUnreadMentions = useCallback(() => {
    getAllMessengerRooms(matrixClient).forEach((room) => {
      if (pathnameRef.current.startsWith(`/messenger/${room.roomId}`)) return;

      getUnreadMentionEventsForUser(room, matrixClient).forEach((event) => {
        tryAddMentionNotification(event, room);
      });
    });
  }, [matrixClient, tryAddMentionNotification]);

  // Restore unread mention notifications from Matrix read markers after reload.
  useEffect(() => {
    if (!hasReachedSyncReadyState) return;
    hydrateUnreadMentions();
  }, [hasReachedSyncReadyState, hydrateUnreadMentions]);

  useEffect(() => {
    const sessionStartTime = Date.now();

    const handleTimeline = (event: MatrixEvent, room: Room | undefined, toStartOfTimeline?: boolean) => {
      if (toStartOfTimeline) return;
      if (!room) return;
      if (event.getType() !== 'm.room.message') return;
      // Skip sync-replay events; unread mentions are restored via hydrateUnreadMentions.
      if ((event.getTs() ?? 0) < sessionStartTime) return;

      tryAddMentionNotification(event, room);
    };

    const handleDecrypted = (event: MatrixEvent) => {
      const roomId = event.getRoomId();
      const room = roomId ? matrixClient.getRoom(roomId) ?? undefined : undefined;
      if (!room || event.getType() !== 'm.room.message') return;

      // No sessionStartTime guard — E2EE mentions may decrypt after reload.
      tryAddMentionNotification(event, room);
    };

    matrixClient.on(RoomEvent.Timeline, handleTimeline);
    matrixClient.on(MatrixEventEvent.Decrypted, handleDecrypted);
    return () => {
      matrixClient.off(RoomEvent.Timeline, handleTimeline);
      matrixClient.off(MatrixEventEvent.Decrypted, handleDecrypted);
    };
  }, [matrixClient, tryAddMentionNotification]);

  useEffect(() => {
    const match = location.pathname.match(/^\/messenger\/(![^/]+)/);
    if (!match) return;
    removeNotificationsByPrefix(`messenger-mention-${match[1]}-`);
  }, [location.pathname, removeNotificationsByPrefix]);
};

export default useMentionNotifications;
